import type { CheckFilter, CheckResult } from './checks';
import type { DefinitionSource } from './definition-source';
import { toPercentageDecimal } from './helpers';

export const TRACK_IMPORT_BATCH_SIZE = 100;

export type TrackLevelImportance = 'mandatory' | 'recommended';

/**
 * A single level within a track. Levels group a set of checks that an entity
 * must pass to be considered compliant at that level.
 */
export type TrackLevel = {
  /** Uniquely identifies and orders the level within a track (1-based). */
  ordinal: number;
  /** Optional additional context about the level. */
  description?: string;
  /** How this level contributes to track compliance. */
  importance: TrackLevelImportance;
  /** Ids of the checks (see {@link CheckDefinition.id}) belonging to this level. */
  checkIds: string[];
};

/**
 * Full track definition shared between authoring, transport, and execution.
 * A track organizes existing checks into ordered levels.
 */
export type TrackDefinition = {
  id: string;
  ownerEntityRef: string;
  name: string;
  /** ISO timestamp when a UI-managed track was created. */
  createdAt?: string;
  /** ISO timestamp when a UI-managed track was last updated. */
  updatedAt?: string;
  /** Origin of the definition; populated by the backend at runtime. */
  source?: DefinitionSource;
  description?: string;
  /** Optional link users can follow for more details about this track. */
  documentationUrl?: string;
  /** Optional authoring metadata; omitted means the track is active. */
  inactive?: boolean;
  /** Includes only entities that match at least one of these filter rules. */
  filter?: CheckFilter[];
  /** Excludes entities that match at least one of these exclude rules. */
  exclude?: CheckFilter[];
  /** Ordered levels that make up the track. */
  levels: TrackLevel[];
};

/** A track that could not be created during a batch YAML import. */
export type TrackImportFailure = {
  id?: string;
  reason: string;
};

/** Per-item outcome returned after processing a track YAML import. */
export type TrackImportResult = {
  total: number;
  imported: string[];
  skipped: string[];
  failed: TrackImportFailure[];
};

/**
 * Independent per-level status for a single entity.
 */
export type TrackLevelResult = {
  ordinal: number;
  passedChecks: number;
  totalChecks: number;
  /** Share of passing checks in the level, 0-100. */
  percentage: number;
  /** True when every check in the level passes. */
  passed: boolean;
};

/**
 * Per-entity result produced by evaluating a track definition against the
 * entity's latest check results.
 */
export type TrackResult = {
  trackId: string;
  entityRef: string;
  /**
   * Authoritative certification result. True when every mandatory level
   * passes. Optional while persisted results from older versions remain valid.
   */
  trackPassed?: boolean;
  /**
   * Highest cumulative level the entity has achieved. A level counts as
   * achieved only when it and every lower level fully pass. `0` means no
   * level has been achieved.
   */
  achievedLevel: number;
  /** Total passing checks across all levels. */
  overallPassedChecks: number;
  /** Total checks across all levels. */
  overallTotalChecks: number;
  /** Share of passing checks across the whole track, 0-100 (rounded to one decimal place). */
  overallPercentage: number;
  levels: TrackLevelResult[];
};

/**
 * Computes a track's per-entity result from its definition and latest check
 * results. Shared by the backend `TrackEngine` (authoritative scoring) and
 * the frontend (recomputing after an ad-hoc check refresh) so both stay in
 * sync.
 *
 * Each level is scored independently as a share of passing checks. The
 * achieved level is cumulative: a level counts as achieved only when it and
 * every lower level fully pass.
 */
export const computeTrackResult = (
  track: TrackDefinition,
  entityRef: string,
  checkResults: CheckResult[],
  excludedCheckIds: ReadonlySet<string> = new Set(),
): TrackResult => {
  const passedByCheckId = new Map(
    checkResults.map(checkResult => [checkResult.checkId, checkResult.passed]),
  );

  const sortedLevels = [...track.levels].sort(
    (left, right) => left.ordinal - right.ordinal,
  );

  const levels: TrackLevelResult[] = sortedLevels.map(level => {
    const scoredCheckIds = level.checkIds.filter(
      checkId => !excludedCheckIds.has(checkId),
    );
    const totalChecks = scoredCheckIds.length;
    const passedChecks = scoredCheckIds.filter(
      checkId => passedByCheckId.get(checkId) === true,
    ).length;

    return {
      ordinal: level.ordinal,
      passedChecks,
      totalChecks,
      percentage:
        totalChecks === 0
          ? 100
          : Math.round((passedChecks / totalChecks) * 100),
      passed: passedChecks === totalChecks,
    };
  });

  const hasMandatoryCheck = sortedLevels.some(
    level =>
      level.importance === 'mandatory' &&
      level.checkIds.some(checkId => !excludedCheckIds.has(checkId)),
  );
  const mandatoryLevelsPassed = levels.every(
    (levelResult, index) =>
      sortedLevels[index].importance !== 'mandatory' || levelResult.passed,
  );

  let achievedLevel = 0;
  for (const level of levels) {
    if (!level.passed) {
      break;
    }
    achievedLevel = level.ordinal;
  }

  const overallPassedChecks = levels.reduce(
    (sum, level) => sum + level.passedChecks,
    0,
  );
  const overallTotalChecks = levels.reduce(
    (sum, level) => sum + level.totalChecks,
    0,
  );

  return {
    trackId: track.id,
    entityRef,
    trackPassed: hasMandatoryCheck && mandatoryLevelsPassed,
    achievedLevel,
    overallPassedChecks,
    overallTotalChecks,
    overallPercentage: toPercentageDecimal(
      overallPassedChecks,
      overallTotalChecks,
    ),
    levels,
  };
};

/**
 * Number of entities certified at a given level on a given day.
 */
export type TrackStatusHistoryLevelCount = {
  /** Level ordinal, or `0` for entities that achieved no level. */
  ordinal: number;
  entityCount: number;
};

/**
 * Daily snapshot of the achieved-level distribution for a track.
 */
export type TrackStatusHistoryPoint = {
  /** ISO date string (YYYY-MM-DD). */
  date: string;
  levels: TrackStatusHistoryLevelCount[];
};
